import {useState, useCallback, useEffect, useRef} from "react";
import {useApi} from "@buildwitt/shapeshift-identity";
import {StrapiErrorResponse} from "@buildwitt/shapeshift-identity/types/IAPI";
import dayjs from "dayjs";
import { getDateTo } from "@/pages/ManagerView/Pages/TeamProgress/utils/common-utils";

interface ApiResult {
	isLoading: boolean;
	isError: boolean;
	error?: StrapiErrorResponse;
	statusCode?: number;
}

interface LessonsCompletedChartDto {
	axisDataType: "day" | "weekOfYear";
	tickInterval: string[];
	data: Array<{
		date: string;
		lessonsCompleted: number;
	}>;
}

interface UseLessonsCompletedChartResult {
	lessonsCompletedChartData: LessonsCompletedChartDto | null;
	apiResult: ApiResult;
	getLessonsCompletedChartData: (startDate: Date, endDate: Date, archivedUsers: boolean, options?: { throttle?: boolean; cache?: boolean }) => Promise<void>;
}

const useLessonsCompletedChart = (): UseLessonsCompletedChartResult => {
	const [lessonsCompletedChartData, setLessonsCompletedChart] = useState<LessonsCompletedChartDto | null>(null);
	const [apiResponse, submitApiCall] = useApi<LessonsCompletedChartDto>();
	const abortControllerRef = useRef<AbortController | null>(null);
	const getLessonsCompletedChartData = useCallback(async (
		startDate: Date,
		endDate: Date,
		archivedUsers: boolean,
		options = {throttle: true, cache: true}
	) => {

		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		abortControllerRef.current = new AbortController();

		submitApiCall({
			url: "/api/v1/team-progress/chart",
			method: "GET",
			config: {
				params: {
					dateFrom: dayjs(startDate).format("YYYY-MM-DD"),
					dateTo: getDateTo(endDate),
					archivedUsers: archivedUsers
				},
				signal: abortControllerRef.current.signal
			},
			cache: options.cache,
			throttle: options.throttle
		});
	}, [submitApiCall]);

	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	useEffect(() => {
		if (apiResponse.statusCode === 200 && apiResponse.data) {
			setLessonsCompletedChart(apiResponse.data);
		} else if (apiResponse.statusCode && apiResponse.statusCode !== 200) {
			setLessonsCompletedChart(null);
		}
	}, [apiResponse]);

	return {
		lessonsCompletedChartData,
		apiResult: {
			isLoading: apiResponse.isLoading,
			isError: apiResponse.isError,
			error: apiResponse.errorData,
			statusCode: apiResponse.statusCode
		},
		getLessonsCompletedChartData
	};
};

export default useLessonsCompletedChart;